import { useAsync } from 'react-use';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { Entity } from '@backstage/catalog-model';
import { stringifyEntityRef } from '@backstage/catalog-model';

export const VANTAGE_REPORT_TOKEN_ANNOTATION = 'vantage.sh/report-token';

export class VantageReportNotFoundError extends Error {
  readonly name = 'VantageReportNotFoundError';
  constructor(message: string) {
    super(message);
  }
}

export function hasVantageReport(entity: Entity): boolean {
  return Boolean(
    entity.metadata.annotations?.[VANTAGE_REPORT_TOKEN_ANNOTATION],
  );
}

export interface CostTrendPoint {
  date: string;
  amount: number;
}

export interface CostBreakdownColumn {
  key: string;
  label: string;
}

export interface CostBreakdownItem {
  dimensions: Record<string, string>;
  amount: number;
  previousAmount?: number;
  percentage: number;
}

export interface CostSummary {
  total: { amount: number; currency: string };
  previousTotal?: number;
  periodStart: string;
  periodEnd: string;
  trend: CostTrendPoint[];
  breakdown: CostBreakdownItem[];
  breakdownColumns: CostBreakdownColumn[];
  reportTitle: string;
  reportUrl: string;
}

export function useVantageCost(
  entity: Entity,
  windowDays = 30,
): {
  loading: boolean;
  error: Error | undefined;
  data: CostSummary | undefined;
} {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const entityRef = stringifyEntityRef(entity);

  const {
    loading,
    error,
    value: data,
  } = useAsync(async () => {
    if (!hasVantageReport(entity)) return undefined;

    const baseUrl = await discoveryApi.getBaseUrl('vantage');
    const params = new URLSearchParams({
      entityRef,
      window: String(windowDays),
    });
    const response = await fetchApi.fetch(`${baseUrl}/cost-summary?${params}`);
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const message =
        body.error?.message ??
        body.message ??
        `Vantage API error: ${response.status}`;
      if (response.status === 404) {
        throw new VantageReportNotFoundError(message);
      }
      throw new Error(message);
    }
    return (await response.json()) as CostSummary;
  }, [entityRef, windowDays]);

  return { loading, error, data };
}
